import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Divider,
  Link,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Category as CategoryIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`clinical-tabpanel-${index}`}
      aria-labelledby={`clinical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface ClinicalRecommendation {
  id: string;
  title: string;
  category: string;
  date: string;
  authors: string[];
  description: string;
  url: string;
  isBookmarked?: boolean;
  status: 'active' | 'draft' | 'archived';
  specialty: string[];
  keywords: string[];
}

const ClinicalRecommendations: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<ClinicalRecommendation[]>([]);
  const [bookmarkedItems, setBookmarkedItems] = useState<string[]>([]);

  // Категории клинических рекомендаций
  const categories = [
    { id: 'all', name: 'Все', count: 0 },
    { id: 'pediatrics', name: 'Педиатрия', count: 0 },
    { id: 'cardiology', name: 'Кардиология', count: 0 },
    { id: 'neurology', name: 'Неврология', count: 0 },
    { id: 'endocrinology', name: 'Эндокринология', count: 0 },
    { id: 'pulmonology', name: 'Пульмонология', count: 0 },
    { id: 'gastroenterology', name: 'Гастроэнтерология', count: 0 },
    { id: 'dermatology', name: 'Дерматология', count: 0 },
    { id: 'infectious', name: 'Инфекционные болезни', count: 0 },
    { id: 'emergency', name: 'Неотложная помощь', count: 0 },
  ];

  // Моковые данные клинических рекомендаций (в реальном приложении будут загружаться с API)
  const mockRecommendations: ClinicalRecommendation[] = [
    {
      id: '1',
      title: 'Клинические рекомендации по диагностике и лечению бронхиальной астмы у детей',
      category: 'pediatrics',
      date: '2024-01-15',
      authors: ['Российское респираторное общество', 'Союз педиатров России'],
      description: 'Рекомендации по диагностике, лечению и профилактике бронхиальной астмы у детей различных возрастных групп',
      url: 'https://cr.minzdrav.gov.ru/recommend/123',
      status: 'active',
      specialty: ['Педиатрия', 'Пульмонология'],
      keywords: ['астма', 'дети', 'дыхание', 'аллергия']
    },
    {
      id: '2',
      title: 'Клинические рекомендации по ведению детей с врожденными пороками сердца',
      category: 'pediatrics',
      date: '2024-02-20',
      authors: ['Ассоциация детских кардиологов России'],
      description: 'Современные подходы к диагностике, лечению и наблюдению детей с врожденными пороками сердца',
      url: 'https://cr.minzdrav.gov.ru/recommend/124',
      status: 'active',
      specialty: ['Педиатрия', 'Кардиология'],
      keywords: ['порок сердца', 'врожденный', 'кардиология', 'дети']
    },
    {
      id: '3',
      title: 'Клинические рекомендации по диагностике и лечению сахарного диабета 1 типа у детей',
      category: 'endocrinology',
      date: '2024-03-10',
      authors: ['Российская ассоциация эндокринологов'],
      description: 'Алгоритмы диагностики, лечения и мониторинга сахарного диабета 1 типа в детском возрасте',
      url: 'https://cr.minzdrav.gov.ru/recommend/125',
      status: 'active',
      specialty: ['Педиатрия', 'Эндокринология'],
      keywords: ['диабет', 'инсулин', 'глюкоза', 'дети']
    },
    {
      id: '4',
      title: 'Клинические рекомендации по ведению детей с эпилепсией',
      category: 'neurology',
      date: '2024-01-30',
      authors: ['Российская противоэпилептическая лига'],
      description: 'Современные принципы диагностики, лечения и реабилитации детей с эпилептическими приступами',
      url: 'https://cr.minzdrav.gov.ru/recommend/126',
      status: 'active',
      specialty: ['Педиатрия', 'Неврология'],
      keywords: ['эпилепсия', 'приступы', 'неврология', 'дети']
    },
    {
      id: '5',
      title: 'Клинические рекомендации по диагностике и лечению пневмонии у детей',
      category: 'pulmonology',
      date: '2024-02-15',
      authors: ['Российское респираторное общество'],
      description: 'Алгоритмы диагностики, дифференциальной диагностики и лечения пневмонии у детей',
      url: 'https://cr.minzdrav.gov.ru/recommend/127',
      status: 'active',
      specialty: ['Педиатрия', 'Пульмонология'],
      keywords: ['пневмония', 'инфекция', 'дыхание', 'дети']
    },
    {
      id: '6',
      title: 'Клинические рекомендации по ведению детей с атопическим дерматитом',
      category: 'dermatology',
      date: '2024-03-05',
      authors: ['Российское общество дерматовенерологов и косметологов'],
      description: 'Современные подходы к диагностике, лечению и профилактике атопического дерматита у детей',
      url: 'https://cr.minzdrav.gov.ru/recommend/128',
      status: 'active',
      specialty: ['Педиатрия', 'Дерматология'],
      keywords: ['атопический дерматит', 'кожа', 'аллергия', 'дети']
    },
    {
      id: '7',
      title: 'Клинические рекомендации по неотложной помощи при анафилактическом шоке у детей',
      category: 'emergency',
      date: '2024-01-25',
      authors: ['Российская ассоциация аллергологов и клинических иммунологов'],
      description: 'Алгоритмы неотложной помощи при анафилактических реакциях у детей различных возрастных групп',
      url: 'https://cr.minzdrav.gov.ru/recommend/129',
      status: 'active',
      specialty: ['Педиатрия', 'Неотложная помощь', 'Аллергология'],
      keywords: ['анафилаксия', 'шок', 'неотложная помощь', 'дети']
    },
    {
      id: '8',
      title: 'Клинические рекомендации по диагностике и лечению целиакии у детей',
      category: 'gastroenterology',
      date: '2024-02-28',
      authors: ['Российская гастроэнтерологическая ассоциация'],
      description: 'Современные принципы диагностики, лечения и диетотерапии целиакии в детском возрасте',
      url: 'https://cr.minzdrav.gov.ru/recommend/130',
      status: 'active',
      specialty: ['Педиатрия', 'Гастроэнтерология'],
      keywords: ['целиакия', 'глютен', 'кишечник', 'дети']
    },
    // Рекомендации Союза педиатров России (утвержденные после 2019 г.)
    {
      id: '9',
      title: 'Атопический дерматит (дети/взрослые)',
      category: 'dermatology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению атопического дерматита',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Дерматология'],
      keywords: ['атопический дерматит', 'кожа', 'аллергия', 'дети']
    },
    {
      id: '10',
      title: 'Аллергический ринит (дети/взрослые)',
      category: 'pulmonology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению аллергического ринита',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Пульмонология', 'Аллергология'],
      keywords: ['аллергический ринит', 'дыхание', 'аллергия', 'дети']
    },
    {
      id: '11',
      title: 'Бронхиальная астма (дети/взрослые)',
      category: 'pulmonology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению бронхиальной астмы',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Пульмонология'],
      keywords: ['бронхиальная астма', 'дыхание', 'аллергия', 'дети']
    },
    {
      id: '12',
      title: 'Гастрит и дуоденит у детей',
      category: 'gastroenterology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению гастрита и дуоденита у детей',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Гастроэнтерология'],
      keywords: ['гастрит', 'дуоденит', 'желудок', 'дети']
    },
    {
      id: '13',
      title: 'Бронхит у детей',
      category: 'pulmonology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению бронхита у детей',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Пульмонология'],
      keywords: ['бронхит', 'дыхание', 'инфекция', 'дети']
    },
    {
      id: '14',
      title: 'Инфекция мочевых путей у детей',
      category: 'pediatrics',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению инфекций мочевых путей у детей',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Урология'],
      keywords: ['инфекция мочевых путей', 'мочеполовая система', 'дети']
    },
    {
      id: '15',
      title: 'Контактный дерматит (дети/взрослые)',
      category: 'dermatology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению контактного дерматита',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Дерматология'],
      keywords: ['контактный дерматит', 'кожа', 'аллергия', 'дети']
    },
    {
      id: '16',
      title: 'Крапивница',
      category: 'dermatology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению крапивницы',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Дерматология', 'Аллергология'],
      keywords: ['крапивница', 'кожа', 'аллергия', 'дети']
    },
    {
      id: '17',
      title: 'Мочекаменная болезнь у детей',
      category: 'pediatrics',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению мочекаменной болезни у детей',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Урология'],
      keywords: ['мочекаменная болезнь', 'камни', 'мочеполовая система', 'дети']
    },
    {
      id: '18',
      title: 'Наружный отит (дети)',
      category: 'pediatrics',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению наружного отита у детей',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Оториноларингология'],
      keywords: ['наружный отит', 'ухо', 'инфекция', 'дети']
    },
    {
      id: '19',
      title: 'Острый бронхиолит',
      category: 'pulmonology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению острого бронхиолита',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Пульмонология'],
      keywords: ['бронхиолит', 'дыхание', 'инфекция', 'дети']
    },
    {
      id: '20',
      title: 'Острый ларингит',
      category: 'pulmonology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению острого ларингита',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Оториноларингология'],
      keywords: ['ларингит', 'гортань', 'дыхание', 'дети']
    },
    {
      id: '21',
      title: 'Острый обструктивный ларингит [круп] и эпиглоттит у детей',
      category: 'emergency',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению крупа и эпиглоттита у детей',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Неотложная помощь', 'Оториноларингология'],
      keywords: ['круп', 'эпиглоттит', 'дыхание', 'неотложная помощь', 'дети']
    },
    {
      id: '22',
      title: 'Острая респираторная вирусная инфекция (ОРВИ)',
      category: 'infectious',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению ОРВИ',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Инфекционные болезни'],
      keywords: ['ОРВИ', 'вирус', 'инфекция', 'дети']
    },
    {
      id: '23',
      title: 'Острый тонзиллофарингит (дети/взрослые)',
      category: 'infectious',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению острого тонзиллофарингита',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Инфекционные болезни', 'Оториноларингология'],
      keywords: ['тонзиллофарингит', 'горло', 'инфекция', 'дети']
    },
    {
      id: '24',
      title: 'Пневмония у детей старше 1 месяца',
      category: 'pulmonology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению пневмонии у детей',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Пульмонология'],
      keywords: ['пневмония', 'дыхание', 'инфекция', 'дети']
    },
    {
      id: '25',
      title: 'Расстройства аутистического спектра (дети)',
      category: 'neurology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению расстройств аутистического спектра',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Неврология', 'Психиатрия'],
      keywords: ['аутизм', 'расстройства аутистического спектра', 'неврология', 'дети']
    },
    {
      id: '26',
      title: 'Хроническая болезнь почек',
      category: 'pediatrics',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению хронической болезни почек',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Нефрология'],
      keywords: ['хроническая болезнь почек', 'почки', 'дети']
    },
    {
      id: '27',
      title: 'Энурез (дети)',
      category: 'pediatrics',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению энуреза у детей',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Урология', 'Неврология'],
      keywords: ['энурез', 'мочеиспускание', 'дети']
    },
    {
      id: '28',
      title: 'Язвенная болезнь желудка и/или двенадцатиперстной кишки у детей',
      category: 'gastroenterology',
      date: '2024-01-01',
      authors: ['Союз педиатров России'],
      description: 'Клинические рекомендации по диагностике и лечению язвенной болезни у детей',
      url: 'https://www.pediatr-russia.ru/information/klin-rek/utverzhdennye-posle-2019/',
      status: 'active',
      specialty: ['Педиатрия', 'Гастроэнтерология'],
      keywords: ['язвенная болезнь', 'желудок', 'двенадцатиперстная кишка', 'дети']
    }
  ];

  // Инициализация данных
  useEffect(() => {
    setRecommendations(mockRecommendations);
    // Загружаем закладки из localStorage
    const savedBookmarks = localStorage.getItem('clinicalRecommendationsBookmarks');
    if (savedBookmarks) {
      setBookmarkedItems(JSON.parse(savedBookmarks));
    }
  }, []);

  // Обновляем счетчики категорий
  useEffect(() => {
    const updatedCategories = categories.map(cat => ({
      ...cat,
      count: cat.id === 'all' 
        ? recommendations.length 
        : recommendations.filter(rec => rec.category === cat.id).length
    }));
    // Здесь можно обновить состояние categories, но для простоты оставляем как есть
  }, [recommendations]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSearch = () => {
    setIsLoading(true);
    // Имитация поиска
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const handleBookmark = (id: string) => {
    const newBookmarks = bookmarkedItems.includes(id)
      ? bookmarkedItems.filter(item => item !== id)
      : [...bookmarkedItems, id];
    
    setBookmarkedItems(newBookmarks);
    localStorage.setItem('clinicalRecommendationsBookmarks', JSON.stringify(newBookmarks));
  };

  const filteredRecommendations = recommendations.filter(rec => {
    const matchesSearch = rec.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rec.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rec.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#4CAF50';
      case 'draft': return '#FF9800';
      case 'archived': return '#9E9E9E';
      default: return '#757575';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Активные';
      case 'draft': return 'Черновики';
      case 'archived': return 'Архивные';
      default: return 'Неизвестно';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h2" sx={{ mb: 2, color: '#2D3748', fontWeight: 700 }}>
          📋 Клинические рекомендации
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096', maxWidth: 800, fontSize: '1.1rem' }}>
          Научно-обоснованные клинические рекомендации по педиатрии и смежным специальностям. 
          Интеграция с официальным реестром Минздрава России.
        </Typography>
      </Box>

      <Alert 
        severity="info" 
        sx={{ 
          mb: 3, 
          background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
          border: '1px solid #4A90E2',
          '& .MuiAlert-icon': {
            color: '#4A90E2'
          }
        }}
      >
        <Typography variant="body2">
          <strong>📚 Источник:</strong> Официальный реестр клинических рекомендаций Минздрава России 
          <Link href="https://cr.minzdrav.gov.ru/rubricator" target="_blank" rel="noopener noreferrer" 
                 sx={{ color: '#4A90E2', textDecoration: 'none', ml: 1, fontWeight: 600 }}>
            cr.minzdrav.gov.ru
          </Link>
        </Typography>
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="🔍 Поиск рекомендаций" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)', border: '1px solid #DEE2E6' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
              <TextField
                fullWidth
                placeholder="🔍 Поиск по названию, описанию или ключевым словам..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#4A90E2' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  maxWidth: 600,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4A90E2',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4A90E2',
                      borderWidth: 2,
                    },
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={isLoading}
                sx={{ 
                  minWidth: 120,
                  background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #357ABD 0%, #2E5BBA 100%)',
                  }
                }}
              >
                {isLoading ? <CircularProgress size={20} color="inherit" /> : '🔍 Найти'}
              </Button>
            </Box>

          </CardContent>
        </Card>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ color: '#4A90E2' }} />
          </Box>
        ) : (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              mb: 3,
              p: 2,
              backgroundColor: '#F8F9FA',
              borderRadius: 2,
              border: '1px solid #E9ECEF'
            }}>
              <Typography variant="h6" sx={{ color: '#2D3748', fontWeight: 600 }}>
                📊 Найдено рекомендаций: {filteredRecommendations.length}
              </Typography>
            </Box>
            
            {filteredRecommendations.map((recommendation) => (
              <Card 
                key={recommendation.id} 
                sx={{ 
                  mb: 3, 
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  borderRadius: 3,
                  border: '1px solid #E9ECEF',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" sx={{ mb: 2, color: '#2D3748', fontWeight: 700, lineHeight: 1.3 }}>
                        📋 {recommendation.title}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip
                          icon={<CategoryIcon />}
                          label={categories.find(cat => cat.id === recommendation.category)?.name}
                          size="small"
                          sx={{ 
                            backgroundColor: '#E3F2FD', 
                            color: '#4A90E2',
                            fontWeight: 600,
                            border: '1px solid #BBDEFB'
                          }}
                        />
                        <Chip
                          icon={<CalendarIcon />}
                          label={new Date(recommendation.date).toLocaleDateString('ru-RU')}
                          size="small"
                          sx={{ 
                            backgroundColor: '#F3E5F5', 
                            color: '#9C27B0',
                            fontWeight: 600,
                            border: '1px solid #E1BEE7'
                          }}
                        />
                        <Chip
                          label={getStatusText(recommendation.status)}
                          size="small"
                          sx={{ 
                            backgroundColor: getStatusColor(recommendation.status) + '20',
                            color: getStatusColor(recommendation.status),
                            fontWeight: 600,
                            border: `1px solid ${getStatusColor(recommendation.status)}40`
                          }}
                        />
                      </Box>

                      <Typography variant="body1" sx={{ color: '#718096', mb: 3, lineHeight: 1.6, fontSize: '1rem' }}>
                        {recommendation.description}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#2D3748', fontWeight: 600, mb: 1 }}>
                          🏥 Специальности:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {recommendation.specialty.map((spec, index) => (
                            <Chip
                              key={index}
                              label={spec}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.8rem',
                                borderColor: '#4A90E2',
                                color: '#4A90E2',
                                '&:hover': {
                                  backgroundColor: '#E3F2FD'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#2D3748', fontWeight: 600, mb: 1 }}>
                          🏷️ Ключевые слова:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {recommendation.keywords.slice(0, 5).map((keyword, index) => (
                            <Chip
                              key={index}
                              label={keyword}
                              size="small"
                              sx={{ 
                                backgroundColor: '#F8F9FA', 
                                color: '#6C757D',
                                fontSize: '0.75rem',
                                border: '1px solid #E9ECEF'
                              }}
                            />
                          ))}
                          {recommendation.keywords.length > 5 && (
                            <Chip
                              label={`+${recommendation.keywords.length - 5} еще`}
                              size="small"
                              sx={{ 
                                backgroundColor: '#E9ECEF', 
                                color: '#6C757D',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 3, minWidth: 120 }}>
                      <Tooltip title={bookmarkedItems.includes(recommendation.id) ? 'Удалить из закладок' : 'Добавить в закладки'}>
                        <IconButton
                          onClick={() => handleBookmark(recommendation.id)}
                          sx={{ 
                            color: bookmarkedItems.includes(recommendation.id) ? '#FFD700' : '#718096',
                            '&:hover': { 
                              color: '#FFD700',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          {bookmarkedItems.includes(recommendation.id) ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                        </IconButton>
                      </Tooltip>
                      
                      <Button
                        variant="contained"
                        size="medium"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => window.open(recommendation.url, '_blank')}
                        sx={{ 
                          background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                          '&:hover': { 
                            background: 'linear-gradient(135deg, #357ABD 0%, #2E5BBA 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)'
                          },
                          fontWeight: 600,
                          borderRadius: 2,
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        📖 Открыть
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}

            {filteredRecommendations.length === 0 && !isLoading && (
              <Card sx={{ 
                textAlign: 'center', 
                py: 6, 
                background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)',
                border: '1px solid #DEE2E6'
              }}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: '#2D3748', mb: 2, fontWeight: 600 }}>
                    🔍 Рекомендации не найдены
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#718096', fontSize: '1.1rem' }}>
                    Попробуйте изменить поисковый запрос или выберите другую категорию
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </TabPanel>


    </Box>
  );
};

export default ClinicalRecommendations;
